import type { RewardCardType } from '@assembly-web/services';
import parse from 'html-react-parser';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Banner } from '../../../DesignSystem/Feedback/Banner';
import { LoadingSpinner } from '../../../DesignSystem/Feedback/Icons/LoadingSpinner';
import { Modal } from '../../../DesignSystem/Feedback/Modal';
import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { Button } from '../../../DesignSystem/Inputs/Button';
import { HorizontalRule } from '../../../DesignSystem/Layout/HorizontalRule';
import { SearchableSelect } from '../../Extension/SearchableSelect';

const messages = defineMessages({
  redeemReward: {
    defaultMessage: 'Redeem Reward',
    id: 'tTouHd',
  },
  selectGiftCardValue: {
    defaultMessage: 'Select gift card value',
    id: '0KEoIN',
  },
  giftCardRedemptionPolicy: {
    defaultMessage: 'Gift card redemption policy',
    id: 'A2KIl1',
  },
  redeem: {
    defaultMessage: 'Redeem',
    id: 'XSdWHA',
  },
  redeemAnyway: {
    defaultMessage: 'I understand, redeem anyway',
    id: 'MFNSpP',
  },
  close: {
    defaultMessage: 'Close',
    id: 'rbrahO',
  },
  howToUse: {
    defaultMessage: 'How to use:',
    id: 'twPZ+W',
  },
  rewardSecurityCode: {
    defaultMessage: 'Reward security code:',
    id: 'CgZiZK',
  },
  congratulations: {
    defaultMessage: 'Congratulations!',
    id: 'lOzDTt',
  },
  giftCard: {
    defaultMessage: 'Gift card',
    id: '4X0ZI8',
  },
  successfulRedemption: {
    defaultMessage:
      'You have successfully redemeed a {amount} {name} gift card:',
    id: '6jd/sh',
  },
  redemptionInformation: {
    defaultMessage:
      'Once you click ‘Redeem’, your reward will be sent to {email}.',
    id: 'kniKwh',
  },
  redemptionRefundWarning: {
    defaultMessage:
      'IMPORTANT: After clicking ‘Redeem’, Assembly cannot issue a refund for this reward.',
    id: 'aF+zN4',
  },
  insufficientBalance: {
    defaultMessage: 'Insufficient balance to redeem.',
    id: '45Qnq3',
  },
  doneLabel: {
    defaultMessage: 'Done',
    id: 'JXdbo8',
  },
  loadingDenominations: {
    defaultMessage: 'Loading denominations...',
    id: 'mhyCWr',
  },
});

export type RedemptionInProgressProps = {
  options: {
    id: string;
    value: string;
    displayValue: string;
    isDisabled: boolean;
    carrotsValue: string;
  }[];
  rewardCardImage: string;
  rewardName: string;
  rewardCardContent: string;
  redemptionWarningBannerText?: string;
  onRedeemRewardClick: (data: {
    faceValue: number;
    id: string;
    carrotsValue: string;
  }) => void;
  isValidInCurrentCountry: boolean;
  email: string;
  isLoadingDenominations: boolean;
  isCashOutRewardInProgress: boolean;
};

export type RedemptionSuccessfulProps = {
  rewardCardImage: string;
  rewardName: string;
  rewardCardContent: string;
  rewardCardRedemptionCode: string;
  rewardDenomination: string;
  email: string;
};

export enum RedemptionModalState {
  Redeeming = 'redeeming',
  Redeemed = 'redeemed',
}

export type RedemptionModalProps = {
  isOpen: boolean;
  onClose: () => void;
} & (
  | ({
      state: RedemptionModalState.Redeeming;
      rewardType: RewardCardType.GiftCard | RewardCardType.Charity;
    } & RedemptionInProgressProps)
  | ({
      state: RedemptionModalState.Redeemed;
      rewardType: RewardCardType.GiftCard | RewardCardType.Charity;
    } & RedemptionSuccessfulProps)
);
export function RedemptionModal({
  isOpen,
  onClose,
  ...props
}: RedemptionModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={undefined}>
      {props.state === RedemptionModalState.Redeeming && (
        <RedemptionInProgress {...props} />
      )}
      {props.state === RedemptionModalState.Redeemed && (
        <RedemptionSuccessful {...props} handleRedemptionModalClose={onClose} />
      )}
    </Modal>
  );
}

function RedemptionInProgress({
  options,
  rewardCardImage,
  rewardName,
  rewardCardContent,
  redemptionWarningBannerText,
  onRedeemRewardClick,
  isValidInCurrentCountry,
  email,
  isLoadingDenominations,
  isCashOutRewardInProgress,
}: RedemptionInProgressProps) {
  const { formatMessage } = useIntl();

  const [selectedOption, setSelectedOption] = useState<
    | { id: string; value: string; displayValue: string; carrotsValue: string }
    | undefined
  >(undefined);

  return (
    <div className="h-auto">
      <TextStyle variant="xl-medium">
        {formatMessage(messages.redeemReward)}
      </TextStyle>
      <div className="flex w-full flex-col items-center gap-2 p-4">
        <div className="flex w-full justify-center rounded-md">
          <img alt="" className="h-[125px] w-fit" src={rewardCardImage} />
        </div>
        <TextStyle variant="base-medium" className="">
          {rewardName}
        </TextStyle>
      </div>
      <div className="rounded-lg bg-gray-3 p-4">
        <div className="z-20 rounded bg-gray-1">
          <SearchableSelect
            selectOptions={options}
            onChange={(currentSelectedOption) => {
              setSelectedOption(currentSelectedOption);
            }}
            selectedOption={selectedOption}
            displayValue={(item: {
              id: string;
              value: string;
              displayValue: string;
            }) => item.displayValue}
            placeholder={
              isLoadingDenominations
                ? formatMessage(messages.loadingDenominations)
                : options.every((x) => x.isDisabled)
                ? formatMessage(messages.insufficientBalance)
                : formatMessage(messages.selectGiftCardValue)
            }
            isDisabled={
              options.every((x) => x.isDisabled) || isLoadingDenominations
            }
          />
        </div>
      </div>
      <HorizontalRule className="py-4" />
      <TextStyle variant="sm-medium">
        {formatMessage(messages.giftCardRedemptionPolicy)}
      </TextStyle>
      <div className="max-h-[200px] overflow-scroll">
        <TextStyle className="pb-4" variant="xs-regular">
          {formatMessage(messages.redemptionInformation, { email })}
        </TextStyle>
        <TextStyle className="pb-4" variant="xs-regular">
          {formatMessage(messages.redemptionRefundWarning)}
        </TextStyle>
        <TextStyle className="break-all pb-4" variant="xs-regular">
          {parse(rewardCardContent)}
        </TextStyle>
      </div>
      <HorizontalRule className="pb-4" />
      {redemptionWarningBannerText && (
        <Banner
          className="rounded-xl"
          status={isValidInCurrentCountry ? 'info' : 'error'}
        >
          <TextStyle variant="xs-regular">
            {redemptionWarningBannerText}
          </TextStyle>
        </Banner>
      )}
      <div className="py-2">
        <Button
          disabled={!selectedOption}
          onClick={() => {
            onRedeemRewardClick({
              faceValue: parseInt(selectedOption?.value ?? '0', 10),
              id: selectedOption?.id ?? '',
              carrotsValue: selectedOption?.carrotsValue ?? '0',
            });
          }}
          isLoading={isCashOutRewardInProgress}
          className="w-full"
          variation={
            isValidInCurrentCountry ? 'primary' : 'secondaryEmphasized'
          }
        >
          {isValidInCurrentCountry
            ? formatMessage(messages.redeem)
            : formatMessage(messages.redeemAnyway)}
          {isCashOutRewardInProgress && <LoadingSpinner className="mr-2" />}
        </Button>
      </div>
    </div>
  );
}

function RedemptionSuccessful({
  rewardCardImage,
  rewardName,
  rewardCardContent,
  rewardDenomination,
  handleRedemptionModalClose,
  rewardCardRedemptionCode,
}: RedemptionSuccessfulProps & {
  handleRedemptionModalClose: () => void;
}) {
  const { formatMessage } = useIntl();

  return (
    <div className="h-auto">
      <TextStyle variant="lg-medium">
        {formatMessage(messages.congratulations)}
      </TextStyle>
      <div className="flex w-full flex-col items-center gap-2 py-4">
        <div className="flex w-full justify-center rounded-md">
          <img alt="" className="h-[125px] w-fit" src={rewardCardImage} />
        </div>
        <TextStyle variant="base-medium">
          {formatMessage(messages.successfulRedemption, {
            name: rewardName,
            amount: rewardDenomination,
          })}
        </TextStyle>
      </div>
      <div className="rounded-lg bg-gray-3 p-4">
        <TextStyle variant="base-medium">
          {formatMessage(messages.rewardSecurityCode)}
        </TextStyle>
        <TextStyle variant="xs-regular">{rewardCardRedemptionCode}</TextStyle>
      </div>
      <HorizontalRule className="py-4" />
      <TextStyle variant="sm-medium">
        {formatMessage(messages.howToUse)}
      </TextStyle>
      <TextStyle
        className="max-h-[90px] overflow-scroll pb-4"
        variant="xs-regular"
      >
        {parse(rewardCardContent)}
      </TextStyle>
      <HorizontalRule className="pb-4 pt-2" />
      <div className="py-2">
        <Button
          onClick={handleRedemptionModalClose}
          className="w-full"
          variation="secondaryEmphasized"
        >
          {formatMessage(messages.doneLabel)}
        </Button>
      </div>
    </div>
  );
}
